/**
 * useAIProviderStatus
 * -------------------
 *
 * September 2026
 *
 * Read-only view of this session's AI provider connection (GET
 * /api/ai/key/status), for pages that use the AI but don't configure it --
 * connecting/disconnecting lives on the AI Settings page (AIProviderPanel).
 */
import * as React from "react";
import axios from "axios";

const PROVIDER_LABELS = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  openrouter: "OpenRouter",
  local: "Local",
  custom: "Custom endpoint",
};

// Names the local engine from its URL, e.g. "Ollama -- llama3".
function classifyRoute(baseUrl) {
  if (!baseUrl) return null;
  const url = baseUrl.toLowerCase();
  if (url.includes("model-runner.docker.internal") || url.includes("/engines/")) return "Docker Model Runner";
  if (url.includes("ollama") || url.includes(":11434")) return "Ollama";
  if (url.includes("/api/chat/completions")) return "Open WebUI";
  return "Custom endpoint";
}

/** Normalizes a /api/ai/key/status-shaped response into the status object the UI uses. */
export function toProviderStatus(data) {
  const hasKey = !!data?.has_key;
  const provider = hasKey ? data.provider : null;
  const baseUrl = hasKey ? data.base_url : null;
  return {
    hasKey,
    provider,
    baseUrl,
    model: hasKey ? data.model : null,
    routeLabel: ["local", "custom"].includes(provider) ? classifyRoute(baseUrl) : null,
  };
}

// Window event fired when the AI Settings page connects, disconnects or switches provider.
export const AI_PROVIDER_CHANGED = "desma:ai-provider-changed";

/** Tells listeners (e.g. the AI Assistant chat) about a new provider status. */
export function announceProviderStatus(status) {
  window.dispatchEvent(new CustomEvent(AI_PROVIDER_CHANGED, { detail: status }));
}

/** "OpenRouter -- meta-llama/llama-3.3-70b-instruct:free", "Ollama -- llama3", "Anthropic", ... */
export function describeProviderStatus(status) {
  const label = status.routeLabel || PROVIDER_LABELS[status.provider] || status.provider;
  return status.model ? `${label} -- ${status.model}` : label;
}

export default function useAIProviderStatus() {
  const [status, setStatus] = React.useState({ ...toProviderStatus(null), loaded: false });

  React.useEffect(() => {
    axios
      .get("/api/ai/key/status")
      .then((res) => setStatus({ ...toProviderStatus(res.data), loaded: true }))
      .catch(() => setStatus({ ...toProviderStatus(null), loaded: true }));
  }, []);

  return status;
}
